import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Dialog, DialogBody, DialogFooter, Button } from '@ecster/components';
import { setCookie, getCookie } from '@ecster/cookies';
import { i18n } from '@ecster/i18n';

import { hideOnboarding } from '../redux/actions';

// increase value if we want all users to see the onboarding dialog again
const onboardingVersion = 4; // 4 for secure-id release

const seenAlreadyKey = (customerId, locale) => `onboarding-info-${customerId}-${locale}-${onboardingVersion}`;

const isIE11 = !!window.navigator.userAgent.match(/trident\/7/i);

const setSeenAlready = customerId => {
    const {
        EcsterConfig: { locale },
    } = window;
    const key = seenAlreadyKey(customerId, locale);

    setCookie(key, true, { days: 365 * 50 });
};

const getSeenAlready = (customerId, callback) => {
    const {
        EcsterConfig: { locale },
    } = window;
    const key = seenAlreadyKey(customerId, locale);

    callback(getCookie(key) === true);
};

class OnboardingDialog extends Component {
    buttonRef = React.createRef();

    static propTypes = {
        hideOnboarding: PropTypes.func.isRequired,
        showDialogAgain: PropTypes.bool, // overrides cookie
        customerId: PropTypes.number.isRequired,
        showDialog: PropTypes.func, // optional function
    };

    static defaultProps = {
        showDialogAgain: false,
        showDialog: () => true,
    };

    state = {
        openDialog: false,
    };

    componentDidMount() {
        const { customerId } = this.props;

        getSeenAlready(customerId, seenAlready => {
            if (!seenAlready) {
                document.addEventListener('keydown', this.escFunction, false);
                this.setState({ openDialog: true });
            } else {
                document.removeEventListener('keydown', this.escFunction);
            }
        });
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.showDialogAgain) {
            // show again after click on beta or beta label
            this.setState({ openDialog: true });
        }
    }

    componentDidUpdate() {
        const { openDialog } = this.state;
        if (openDialog && this.buttonRef.current) {
            // grab focus to avoid tabbing around in app behind overlay
            this.buttonRef.current.focus();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.escFunction);
    }

    escFunction = event => {
        if (event.key === 'Escape') {
            this.onClickGo();
        }
    };

    onBlur = () => {
        // re-grab focus to avoid tabbing again
        this.buttonRef.current.focus();
    };

    onClickGo = () => {
        const { hideOnboarding, customerId } = this.props;
        this.setState({ openDialog: false });
        hideOnboarding();
        setSeenAlready(customerId);
    };

    render() {
        return this.renderDialog(<p>&nbsp;</p>); // add content here
    }

    renderDialog = body => {
        const { openDialog } = this.state;
        const { showDialog } = this.props;

        if (isIE11 || !openDialog || !showDialog()) return null;

        return (
            <Dialog rounded5x maxWidth="500px" fullscreenMobile open={openDialog} className="common-onboarding-dialog">
                <DialogBody className="dialog">{body}</DialogBody>
                <DialogFooter>
                    <Button
                        round
                        onClick={this.onClickGo}
                        onBlur={this.onBlur}
                        name="onboarding-lets-go"
                        ref={this.buttonRef}
                    >
                        {i18n('common.onboarding.button-text')}
                    </Button>
                </DialogFooter>
            </Dialog>
        );
    };
}

const mapStateToProps = ({ common, authentication }) => ({
    showDialogAgain: common.onboarding && common.onboarding.show,
    customerId: authentication.person && authentication.person.id,
    firstName: authentication.person && authentication.person.firstName,
});

const mapDispatchToProps = dispatch => ({
    hideOnboarding: () => dispatch(hideOnboarding()),
});

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingDialog);
